'use client'

import Spinner from '@/components/Spinner';
import dynamic from 'next/dynamic';
/***
 *
 * @file blocks/EmbeddedContainer/index.tsx
 * @description EmbeddedContainer component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Centres
 * @since 17 Oct 2025
 *
 */

import React, { useEffect, useRef, useState } from 'react';
// import Embed from './Embed';
// import ImageWrapper from '@/components/ImageWrapper';

const Embed = dynamic(()=>import("./Embed"), {ssr: false, loading: () => <Spinner loading={true}><></></Spinner>});
const ImageWrapper = dynamic(()=>import("@/components/ImageWrapper"), {ssr: false});

/**
 * Generic props interface for EmbeddedContainer
 * 
 * @typeParam T - optional extra props for extending the base component
 */
export interface EmbeddedContainerProps<T = unknown> extends React.HTMLAttributes<HTMLDivElement> {
    /** Optional React children rendered inside the container */
    children?: React.ReactNode;

    /** image source url */
    src?: string,

    /** DAM image source url */
    damSrc?: {
        "type": string,
        "title": string,
        "thumbnail": string,
        "originalUrl": string
    },

    /** DAM thumbnail source url */
    damThumbnail?: {
        "type": string,
        "title": string,
        "thumbnail": string,
        "originalUrl": string
    },

    /** HTML embed code string or actual HTMLElement */
    embedCode?: string | HTMLElement;

    /** Optional ID for the root element */
    id?: string;

    /** Inline styles for the outer container */
    style?: React.CSSProperties;

    /** Additional custom props if needed */
    extraProps?: T;
}

/**
 * A reusable, type-safe component that safely injects third-party embed code
 * (such as iframes, scripts, widgets, etc.) into the DOM.
 *
 * Automatically re-renders when `embedCode` changes and cleans up on unmount.
 */
export const EmbeddedContainer = <T,>({
    children,
    src,
    damSrc,
    damThumbnail,
    ...rest
}: EmbeddedContainerProps<T>): React.ReactElement => {
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        // const timer = setTimeout(() => {
            if (typeof window !== "undefined") {
                if(document.readyState) {
                    setShouldRender(true);
                }
            }
        // }, 500); // or after hydration
        // return () => clearTimeout(timer);
    }, []);


    return (
        <>
            {/* Show poster as static image for LCP */}
            {(src || damSrc || damThumbnail) && 
                // <img
                //     src={posterImage}
                //     alt="Embedded poster"
                //     style={{ width: "100%", height: "auto" }}
                //     fetchPriority="high"
                // />
                <ImageWrapper
                    src={src}
                    damSrc={damSrc}
                    damThumbnail={damThumbnail}
                    alt="Embedded poster"
                    style={{ width: "100%", height: "auto" }}
                    fetchPriority="high"
                    loader={({ src }) => src}
                />
            }

            {/* Inject the full embed later */}
            {shouldRender && 
                <Embed
                    {...rest}
                >
                    {children}
                </Embed>
            }
        </>
    );
};

export default EmbeddedContainer;
